import { useContext, useEffect, useMemo, useRef } from 'react'
import { context as contextoDoCanvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { alvoDoFio, avancarFio, criarFio, formaPendente, PARTICULAS_POR_FIO, type FioDeFita, type V3 } from './fisicaAnelDeFita'

/**
 * O anel de fita costurado, como se monta: um laço fechado. A fita chata dá a
 * volta por trás do apoio (o banzo da escada, o tronco), os dois fios do anel
 * dobrado descem até o mosquetão e o laço abraça a barra dele, por dentro (é
 * ali que o mosquetão puxa). Na barra a fita se franze e deita ao longo do
 * metal; no caminho, ela se torce do banzo até a barra. Numa das pernas, a
 * costura (a barra de pontos que fecha o anel) aparece mais escura e mais grossa.
 *
 * Cada fio é simulado (ver `fisicaAnelDeFita.ts`): sem carga a fita sobra e
 * pende; com carga ela estica reta até o mosquetão. Fora de um `<Canvas>`
 * (testes, servidor) o anel fica na forma de repouso, sem simulação.
 */

/** Largura e espessura da fita na cena (exageradas como a corda, para se ler no celular). */
export const LARGURA_FITA_M = 0.036
export const ESPESSURA_FITA_M = 0.005
/** Pontos na volta em torno do apoio. */
const PONTOS_NA_VOLTA = 9
/** Onde fica a costura, na segunda perna, de 0 (apoio) a 1 (mosquetão). */
const COSTURA: readonly [number, number] = [0.42, 0.56]
/** Um ciclo do trançado da fita, em metros. */
const PERIODO_TRAMA_M = 0.035

type V = readonly [number, number, number]

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const soma = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const esc = (a: V, k: number): V => [a[0] * k, a[1] * k, a[2] * k]
const dot = (a: V, b: V) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const cross = (a: V, b: V): V => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const comp = (a: V) => Math.hypot(a[0], a[1], a[2])
const unit = (a: V): V => {
  const c = comp(a)
  return c > 1e-12 ? esc(a, 1 / c) : [0, 0, 0]
}

/** Um vetor unitário qualquer perpendicular a `a`. */
const perpendicular = (a: V): V => unit(Math.abs(a[1]) < 0.9 ? cross(a, [0, 1, 0]) : cross(a, [1, 0, 0]))

interface Volta {
  /** Onde a fita sai do apoio, de cada lado. */
  readonly saidas: readonly [V, V]
  /** Os pontos da volta por trás do apoio, de uma saída à outra, sem as saídas. */
  readonly pontos: readonly V[]
  /** A direção do apoio: a largura da fita fica deitada nela. */
  readonly eixo: V
}

/**
 * A volta da fita em torno de um apoio de centro `apoio` e direção `eixo`
 * (o banzo, a barra do mosquetão): os dois fios saem dos lados do apoio,
 * perpendiculares ao rumo de `para`, e a fita passa por trás (do lado oposto
 * a `para`, que é para onde ela é puxada contra o apoio).
 */
function voltaNoApoio(apoio: V, eixoDoApoio: V | null, raio: number, para: V, raioMinimo = 0.012): Volta {
  const rumo = sub(para, apoio)
  let eixo = eixoDoApoio ? unit(eixoDoApoio) : unit(cross(rumo, [0, 1, 0]))
  if (comp(eixo) < 0.5) eixo = perpendicular(unit(rumo))
  if (comp(eixo) < 0.5) eixo = [1, 0, 0]
  let p = unit(sub(rumo, esc(eixo, dot(rumo, eixo))))
  if (comp(p) < 0.5) p = perpendicular(eixo)
  const lado = unit(cross(eixo, p))
  const r = Math.max(raio + ESPESSURA_FITA_M, raioMinimo)
  const pontos: V[] = []
  for (let k = 1; k <= PONTOS_NA_VOLTA; k += 1) {
    const th = -Math.PI / 2 + (Math.PI * k) / (PONTOS_NA_VOLTA + 1)
    pontos.push(soma(apoio, soma(esc(p, -r * Math.cos(th)), esc(lado, r * Math.sin(th)))))
  }
  return { saidas: [soma(apoio, esc(lado, -r)), soma(apoio, esc(lado, r))], pontos, eixo }
}

/** A barra do mosquetão que o laço abraça. */
export interface BarraDaFita {
  readonly centro: V
  readonly eixo: V
  readonly raio: number
}

export interface GeometriaDoAnel {
  /** Onde cada fio sai do apoio. */
  readonly saidas: readonly [V, V]
  /** Onde cada fio chega na barra do mosquetão (do mesmo lado que saiu do apoio, sem cruzar). */
  readonly chegadas: readonly [V, V]
  /** A volta no apoio, da saída do fio 1 à do fio 2. */
  readonly voltaNoApoio: readonly V[]
  /** A volta na barra, da chegada do fio 2 à do fio 1. */
  readonly voltaNaBarra: readonly V[]
  readonly eixoDoApoio: V
  /** O eixo da barra, já virado para o mesmo lado do eixo do apoio (a fita não dá meia-volta). */
  readonly eixoDaBarra: V
}

/** Sem barra informada: uma barra de mosquetão no `para`, atravessada ao rumo do apoio. */
function barraPadrao(apoio: V, eixoDoApoio: V | null, para: V, escala: number): BarraDaFita {
  const rumo = unit(sub(apoio, para))
  let eixo = unit(cross(rumo, eixoDoApoio ?? [0, 1, 0]))
  if (comp(eixo) < 0.5) eixo = perpendicular(rumo)
  return { centro: para, eixo, raio: 0.0055 * escala }
}

/** O laço inteiro: a volta no apoio, a volta na barra e onde cada fio começa e termina. */
export function geometriaDoAnel(apoio: V, eixoDoApoio: V | null, raioDoApoio: number, barra: BarraDaFita): GeometriaDoAnel {
  const noApoio = voltaNoApoio(apoio, eixoDoApoio, raioDoApoio, barra.centro)
  // na barra a volta é justa: o raio do metal e a espessura da fita
  const naBarra = voltaNoApoio(barra.centro, barra.eixo, barra.raio, apoio, 0)
  // cada fio vai para o lado da barra mais perto de onde saiu, para os dois não se cruzarem
  const direto = comp(sub(noApoio.saidas[0], naBarra.saidas[0])) + comp(sub(noApoio.saidas[1], naBarra.saidas[1]))
  const cruzado = comp(sub(noApoio.saidas[0], naBarra.saidas[1])) + comp(sub(noApoio.saidas[1], naBarra.saidas[0]))
  const trocar = cruzado < direto
  const chegadas: [V, V] = trocar ? [naBarra.saidas[1], naBarra.saidas[0]] : [naBarra.saidas[0], naBarra.saidas[1]]
  // a volta na barra vai da chegada do fio 2 à do fio 1
  const voltaNaBarra = trocar ? [...naBarra.pontos] : [...naBarra.pontos].reverse()
  const eixoDaBarra = dot(naBarra.eixo, noApoio.eixo) < 0 ? esc(naBarra.eixo, -1) : naBarra.eixo
  return { saidas: noApoio.saidas, chegadas, voltaNoApoio: noApoio.pontos, voltaNaBarra, eixoDoApoio: noApoio.eixo, eixoDaBarra }
}

/** Quantos pontos tem a linha central do anel: os dois fios, as duas voltas e o ponto que fecha o laço. */
const PONTOS_NO_CAMINHO = 2 * PARTICULAS_POR_FIO + 2 * PONTOS_NA_VOLTA + 1

/** Na barra, a fita se franze até esta fração da largura. */
const FRANZIDO_NA_BARRA = 0.5
/** Do banzo (0) à barra (1), a partir de onde a fita começa a se franzir. */
const INICIO_DO_FRANZIDO = 0.7

/** Um ponto do caminho: a posição, o eixo em que a largura deita e quanto da largura sobra. */
interface PontoDoCaminho {
  readonly p: V
  readonly eixo: V
  readonly largura: number
}

/**
 * A linha central do laço fechado: o primeiro fio da barra ao apoio, a volta
 * no apoio, o segundo fio do apoio à barra, a volta na barra e o primeiro
 * ponto de novo. Ao longo dos fios, a largura gira do eixo do apoio para o da
 * barra e se franze perto dela.
 */
function caminhoDoAnel(fio1: ArrayLike<number>, fio2: ArrayLike<number>, anel: GeometriaDoAnel): PontoDoCaminho[] {
  const ponto = (x: ArrayLike<number>, i: number): V => [x[i * 3]!, x[i * 3 + 1]!, x[i * 3 + 2]!]
  const noFio = (x: ArrayLike<number>, i: number): PontoDoCaminho => {
    const u = i / (PARTICULAS_POR_FIO - 1)
    const giro = u * u
    const franze = u <= INICIO_DO_FRANZIDO ? 0 : (u - INICIO_DO_FRANZIDO) / (1 - INICIO_DO_FRANZIDO)
    return {
      p: ponto(x, i),
      eixo: unit(soma(esc(anel.eixoDoApoio, 1 - giro), esc(anel.eixoDaBarra, giro))),
      largura: 1 - (1 - FRANZIDO_NA_BARRA) * franze,
    }
  }
  const caminho: PontoDoCaminho[] = []
  for (let i = PARTICULAS_POR_FIO - 1; i >= 0; i -= 1) caminho.push(noFio(fio1, i))
  for (const p of anel.voltaNoApoio) caminho.push({ p, eixo: anel.eixoDoApoio, largura: 1 })
  for (let i = 0; i < PARTICULAS_POR_FIO; i += 1) caminho.push(noFio(fio2, i))
  for (const p of anel.voltaNaBarra) caminho.push({ p, eixo: anel.eixoDaBarra, largura: FRANZIDO_NA_BARRA })
  caminho.push(caminho[0]!)
  return caminho
}

/** O índice, no caminho, em que começa o segundo fio. */
const INICIO_DO_FIO2 = PARTICULAS_POR_FIO + PONTOS_NA_VOLTA

const naCostura = (j: number) => {
  const u = (j - INICIO_DO_FIO2) / (PARTICULAS_POR_FIO - 1)
  return u >= COSTURA[0] && u <= COSTURA[1]
}

/** A geometria da fita chata: 4 vértices por seção (um retângulo largo e fino), com a topologia fixa. */
export function criarGeometriaDaFita(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry()
  const n = PONTOS_NO_CAMINHO
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 4 * 3), 3))
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(n * 4 * 3), 3))
  const uv = new Float32Array(n * 4 * 2)
  const cores = new Float32Array(n * 4 * 3)
  for (let j = 0; j < n; j += 1) {
    // a costura é uma barra de linha mais escura
    const tom = naCostura(j) ? 0.55 : 1
    for (let c = 0; c < 4; c += 1) {
      uv[(j * 4 + c) * 2 + 1] = c === 1 || c === 2 ? 1 : 0
      cores.set([tom, tom, tom], (j * 4 + c) * 3)
    }
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  g.setAttribute('color', new THREE.BufferAttribute(cores, 3))
  const indices: number[] = []
  for (let j = 0; j < n - 1; j += 1) {
    for (let f = 0; f < 4; f += 1) {
      const a = j * 4 + f
      const b = j * 4 + ((f + 1) % 4)
      const c = a + 4
      const d = b + 4
      indices.push(a, c, b, b, c, d)
    }
  }
  g.setIndex(indices)
  return g
}

/** Põe a fita ao longo do `caminho` fechado, com a largura deitada no eixo de cada ponto. */
export function preencherGeometriaDaFita(g: THREE.BufferGeometry, caminho: readonly PontoDoCaminho[], escala = 1): void {
  const pos = g.getAttribute('position') as THREE.BufferAttribute
  const uv = g.getAttribute('uv') as THREE.BufferAttribute
  const n = caminho.length
  let largura: V = caminho[0]!.eixo
  let comprimento = 0
  for (let j = 0; j < n; j += 1) {
    // o laço é fechado: nas pontas, a tangente usa o outro lado do fechamento
    const anterior = caminho[j === 0 ? n - 2 : j - 1]!.p
    const seguinte = caminho[j === n - 1 ? 1 : j + 1]!.p
    const { eixo } = caminho[j]!
    let t = unit(sub(seguinte, anterior))
    if (comp(t) < 0.5) t = perpendicular(eixo)
    // a largura deita no eixo do ponto (o banzo, a barra), sem a parte ao longo da fita
    let w = unit(sub(eixo, esc(t, dot(eixo, t))))
    if (comp(w) < 0.5) w = largura
    if (dot(w, largura) < 0) w = esc(w, -1)
    largura = w
    const normal = unit(cross(t, w))
    const grossa = naCostura(j) ? 2.4 : 1
    const meiaL = (LARGURA_FITA_M * escala * caminho[j]!.largura) / 2
    const meiaE = (ESPESSURA_FITA_M * escala * grossa) / 2
    const c = caminho[j]!.p
    const cantos: V[] = [
      soma(c, soma(esc(w, -meiaL), esc(normal, meiaE))),
      soma(c, soma(esc(w, meiaL), esc(normal, meiaE))),
      soma(c, soma(esc(w, meiaL), esc(normal, -meiaE))),
      soma(c, soma(esc(w, -meiaL), esc(normal, -meiaE))),
    ]
    if (j > 0) comprimento += comp(sub(c, caminho[j - 1]!.p))
    cantos.forEach((q, k) => {
      pos.setXYZ(j * 4 + k, q[0], q[1], q[2])
      uv.setX(j * 4 + k, comprimento / PERIODO_TRAMA_M)
    })
  }
  pos.needsUpdate = true
  uv.needsUpdate = true
  g.computeVertexNormals()
  g.computeBoundingSphere()
}

let texturaDaTrama: THREE.Texture | null | undefined

/** A trama da fita: nervuras finas atravessando a largura e as bordas mais escuras. Cinza, para tingir com a cor. */
function trama(): THREE.Texture | null {
  if (texturaDaTrama !== undefined) return texturaDaTrama
  texturaDaTrama = null
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null
  const ctx = canvas?.getContext?.('2d') ?? null
  if (canvas && ctx) {
    canvas.width = 32
    canvas.height = 32
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, 32, 32)
    // as nervuras da trama, em espinha de peixe
    for (let x = 0; x < 32; x += 4) {
      ctx.strokeStyle = 'rgba(0,0,0,0.18)'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x + 2, 16)
      ctx.lineTo(x, 32)
      ctx.stroke()
    }
    // as bordas da fita, tecidas mais fechadas
    ctx.fillStyle = 'rgba(0,0,0,0.28)'
    ctx.fillRect(0, 0, 32, 3)
    ctx.fillRect(0, 29, 32, 3)
    texturaDaTrama = new THREE.CanvasTexture(canvas)
    texturaDaTrama.wrapS = THREE.RepeatWrapping
    texturaDaTrama.wrapT = THREE.ClampToEdgeWrapping
    texturaDaTrama.colorSpace = THREE.SRGBColorSpace
  }
  return texturaDaTrama
}

export interface PropsAnelDeFita {
  /** O centro do que a fita abraça (o banzo, o tronco). */
  apoio: V
  /** Onde a fita termina (o mosquetão, o olhal). */
  para: V
  /** A direção do que a fita abraça; sem ela, a fita só converge no apoio. */
  eixo?: V | null
  /** Quanto a fita se afasta do centro do apoio ao dar a volta. */
  raioDoApoio?: number
  /** Com peso a fita estica; sem peso ela pende. */
  carregada: boolean
  /** Comprimento de cada fio do anel sem carga; sem ele, 15% a mais que a distância. */
  comprimentoSemCargaM?: number
  cor: string
  /** Escala da largura e da espessura (destaque dos equipamentos). */
  escala?: number
  /** A barra do mosquetão que o laço abraça; sem ela, uma barra atravessada no `para`. */
  barra?: BarraDaFita | null
}

/** O anel de fita, com a física quando está dentro de um `<Canvas>`. */
export function AnelDeFita({ apoio, para, eixo = null, raioDoApoio = 0, carregada, comprimentoSemCargaM, cor, escala = 1, barra = null }: PropsAnelDeFita) {
  const naCena = useContext(contextoDoCanvas) !== null
  const geometria = useMemo(() => criarGeometriaDaFita(), [])
  useEffect(() => () => geometria.dispose(), [geometria])
  const anel = geometriaDoAnel(apoio, eixo, raioDoApoio, barra ?? barraPadrao(apoio, eixo, para, escala))
  const distancia = comp(sub(para, apoio))
  const semCarga = comprimentoSemCargaM ?? distancia * 1.15
  const alvo = (i: 0 | 1) => alvoDoFio(anel.saidas[i], anel.chegadas[i], semCarga, carregada)

  // a forma de repouso, para o primeiro quadro e fora do Canvas
  const chave = `${apoio.join()}|${para.join()}|${eixo?.join() ?? ''}|${raioDoApoio}|${semCarga}|${carregada}|${escala}|${barra ? [...barra.centro, ...barra.eixo, barra.raio].join() : ''}`
  const ultimaChave = useRef('')
  if (ultimaChave.current !== chave && (!naCena || ultimaChave.current === '')) {
    ultimaChave.current = chave
    const fio = (i: 0 | 1) => formaPendente(anel.saidas[i], anel.chegadas[i], alvo(i), PARTICULAS_POR_FIO).flat()
    preencherGeometriaDaFita(geometria, caminhoDoAnel(fio(0), fio(1), anel), escala)
  }
  if (distancia < 1e-4) return null

  return (
    <mesh name="anel-de-fita" geometry={geometria} raycast={() => null} frustumCulled={false}>
      <meshStandardMaterial color={cor} map={trama()} vertexColors roughness={0.92} side={THREE.DoubleSide} />
      {naCena && (
        <SimulacaoDoAnel
          geometria={geometria}
          anel={anel}
          alvos={[alvo(0), alvo(1)]}
          carregada={carregada}
          semCarga={semCarga}
          escala={escala}
        />
      )}
    </mesh>
  )
}

/** Avança os dois fios a cada quadro e redesenha a fita. */
function SimulacaoDoAnel({
  geometria,
  anel,
  alvos,
  carregada,
  semCarga,
  escala,
}: {
  geometria: THREE.BufferGeometry
  anel: GeometriaDoAnel
  alvos: readonly [number, number]
  carregada: boolean
  semCarga: number
  escala: number
}) {
  const fios = useRef<[FioDeFita, FioDeFita] | null>(null)
  const atual = useRef({ anel, alvos, carregada, escala })
  atual.current = { anel, alvos, carregada, escala }
  if (!fios.current) {
    fios.current = [
      criarFio(anel.saidas[0], anel.chegadas[0], Math.max(alvos[0], semCarga)),
      criarFio(anel.saidas[1], anel.chegadas[1], Math.max(alvos[1], semCarga)),
    ]
  }
  useFrame((_, dt) => {
    const { anel: v, alvos: a, carregada: c, escala: e } = atual.current
    const [f1, f2] = fios.current!
    const passo = Math.min(dt, 1 / 20)
    avancarFio(f1, v.saidas[0] as V3, v.chegadas[0] as V3, a[0], c, passo)
    avancarFio(f2, v.saidas[1] as V3, v.chegadas[1] as V3, a[1], c, passo)
    preencherGeometriaDaFita(geometria, caminhoDoAnel(f1.atual, f2.atual, v), e)
  })
  return null
}
